import { useEffect, useState } from 'react'
import { ModalShell, Field, inputClass, CancelButton, PrimaryButton, ServerError } from '../modal-form'
import { PersonPicker, PersonMultiPicker } from '../shared/PersonPicker'
import { useServiceCrew, useAssignServiceCrew } from '../../hooks/useScheduleV2'
import { usePeople } from '../../hooks/usePeople'
import type { Service, Person } from '../../types'

// AssignCrewModal — spec 15 §4. Single guard + multiple stewards per
// service, with role/dept-filtered pools. Save replaces the full crew
// set for the service.

interface Props {
  service: Service
  onClose: () => void
}

// Guard pool: train crew / guards. We don't yet have a 'Guard' role on
// our v2 stub PEOPLE, so for v2.0 the pool also includes anyone whose
// role text contains 'guard' (case-insensitive). When the people model
// graduates from the stub to dbo.Users, swap to a proper role enum.
function isGuard(p: Person): boolean {
  const role = p.role.toLowerCase()
  return role.includes('guard') || role.includes('train crew')
}

// Steward pool: welcome hosts, customer services, carriage stewards.
function isSteward(p: Person): boolean {
  const dept = p.dept.toLowerCase()
  const role = p.role.toLowerCase()
  return (
    dept.includes('volunteer') ||
    dept.includes('welcome') ||
    dept.includes('customer') ||
    role.includes('welcome host') ||
    role.includes('customer services') ||
    role.includes('carriage steward')
  )
}

export function AssignCrewModal({ service, onClose }: Props) {
  const { data: existing = [] } = useServiceCrew(service.id)
  const save = useAssignServiceCrew(service.id)
  const { data: _people = [] } = usePeople()
  // We don't use _people directly — PersonPicker reads it internally.
  // Kept here as a useful prefetch warmup.

  const [guard, setGuard] = useState<string | null>(null)
  const [stewards, setStewards] = useState<string[]>([])
  const [notes, setNotes] = useState('')
  const [initialised, setInitialised] = useState(false)

  // Initialise from existing crew once the query settles.
  useEffect(() => {
    if (initialised) return
    if (existing.length === 0 && save.status === 'idle') {
      // No crew yet — keep defaults. Mark initialised to avoid re-running.
      setInitialised(true)
      return
    }
    const g = existing.find((c) => c.role === 'guard')?.personId ?? null
    const s = existing.filter((c) => c.role === 'steward').map((c) => c.personId)
    setGuard(g)
    setStewards(s)
    setNotes(existing[0]?.notes ?? '')
    setInitialised(true)
  }, [existing, save.status, initialised])

  function handleSubmit() {
    save.mutate(
      { guard, stewards, notes },
      { onSuccess: () => onClose() }
    )
  }

  const route =
    service.originStation && service.destinationStation
      ? `${service.originStation} → ${service.destinationStation}`
      : service.originStation || service.destinationStation || '—'

  return (
    <ModalShell
      title={`Assign crew — ${service.serviceLabel} · ${service.timeText}`}
      size="xl"
      onClose={onClose}
      footer={
        <>
          <CancelButton onClick={onClose} disabled={save.isPending} />
          <PrimaryButton type="button" onClick={handleSubmit} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save crew'}
          </PrimaryButton>
        </>
      }
      body={
        <div className="space-y-4">
          <div className="text-sm text-ink-soft">
            {service.loco ?? '—'} · {route} · {service.capacity ?? '—'} capacity
          </div>

          <Field label="Guard">
            <PersonPicker
              value={guard}
              onChange={setGuard}
              filter={isGuard}
              placeholder="Pick a guard…"
            />
          </Field>

          <Field label="Carriage stewards">
            <PersonMultiPicker
              value={stewards}
              onChange={setStewards}
              filter={isSteward}
              placeholder="Add a steward…"
            />
          </Field>

          <Field label="Notes" hint="Optional">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className={`${inputClass(false)} resize-y`}
              placeholder="Optional notes for this crew assignment."
            />
          </Field>

          {save.isError && (
            <ServerError title="Could not save crew" error={save.error as Error} />
          )}
        </div>
      }
    />
  )
}
